import { isArray, get, isEqual } from 'lodash';
import { makeAutoObservable } from 'mobx';
import { composeLocationText } from '../utils/formatters';

const LOCATION_KEY = 'CUSTOMER_GEOLOCATION_INFO';

const defaultFilterSettings = {
  availability: [],
  employmentTypes: [],
  tradeTypes: [],
  yearsOfExperience: [],
  militaryStatuses: [],
  languages: [],
  query: '',
  zipCode: '',
  distance: '',
  coordinates: ''

};

const defaultPills = [];

class FilterStore {

  // pill: {field: string, value: string}
  pills = [...defaultPills];

  sharedLocationObject = {};

  // text used in location filter
  sharedLocationText = '';

  filterSettings = { ...defaultFilterSettings };

  filterMetadata = {};

  mobileFilterDrawerOpen = false;

  constructor() {
    makeAutoObservable(this);
  }

  toggleMobileFilterDrawerOpen = () => {
    if (window.screen.width <= 640) {
      this.mobileFilterDrawerOpen = !this.mobileFilterDrawerOpen;
    }
  };

  __updateSharedLocationText = (text) => {
    this.sharedLocationText = text;
  };

  __updateSharedLocationObject = (value) => {
    this.sharedLocationObject = value;
  };

  setProximityFilter = (locationObject, distance = '50mi') => {

    sessionStorage.setItem(LOCATION_KEY, JSON.stringify({ ...locationObject, status: 'ACCEPTED' }));

    this.__updateSharedLocationObject(locationObject);

    this.__updateSharedLocationText(composeLocationText(locationObject.zipCode, locationObject.city, locationObject.state));
    this.addLocationFilter(locationObject);
    distance && this.addFilterParam('distance', distance);
  };

  addLocationFilter(locationObject) {
    this._removeLocationPills();

    if (get(locationObject, 'geolocation.latitude') || get(locationObject, 'geolocation.longitude')) {
      this._updateFilterSettingsField('coordinates', `${locationObject?.geolocation?.latitude},${locationObject?.geolocation.longitude}`);

      this._addPill({ field: 'location', value: composeLocationText(locationObject.zipCode, locationObject.city, locationObject.state) });
    }
  }

  _removeLocationPills() {
    this.pills = this.pills.filter((it) => {
      return get(it, 'field') !== 'location' && get(it, 'field') !== 'distance';
    });

    this._updateFilterSettingsField('distance', '');

  }

  resetProximityFilter = (declinedModal) => {
    if (declinedModal) {
      sessionStorage.setItem(LOCATION_KEY, JSON.stringify({ status: 'DECLINED' }));
    } else {
      sessionStorage.removeItem(LOCATION_KEY);
    }

    this.__updateSharedLocationText('');
    this.__updateSharedLocationObject({});
    this.removeFilterParam('coordinates');
    this._removeLocationPills();
  };

  setFilterMetadata = (metadata) => {
    this.filterMetadata = metadata;
  };

  /**
   * if the field is an array of values, add value to array and add the pill
   * if the field is not an array, update the field with new value and update or add pill
   * @param field
   * @param value
   */
  addFilterParam = (field, value) => {
    if (isArray(this.filterSettings[field])) {
      const newValue = [...this.filterSettings[field], value];
      this._updateFilterSettingsField(field, newValue);
      this._addPill({ field, value });
    } else {
      this._updateFilterSettingsField(field, value);

      this._updatePill(field, value);
    }
  };

  _updatePill = (field, value) => {
    const pill = this.pills.find((it) => it.field === field);

    if (pill) {
      pill.value = value;
    } else {
      this._addPill({ field, value });
    }
  };

  removeFilterParam = (field, value) => {
    if (field === 'location' || field === 'distance') {
      this.resetProximityFilter();
    }
    if (isArray(this.filterSettings[field])) {
      const newValue = this.filterSettings[field].filter((it) => it !== value);
      this._updateFilterSettingsField(field, newValue);
      this._removePill({ field, value });
    } else {
      // fields with primitive value (e.g. zipCode, query, distance)
      const oldValue = this.filterSettings[field];
      this._updateFilterSettingsField(field, '');
      this._removePill({ field, value: oldValue });
    }
  };

  clearAll = () => {
    this.filterSettings = {
      ...defaultFilterSettings
    };
    this.pills = [...defaultPills];
    this.resetProximityFilter();
  };

  _updateFilterSettingsField = (field, value) => {
    this.filterSettings = {
      ...this.filterSettings,
      [field]: value
    };
  };

  _addPill = (pill) => {
    this.pills.push(pill);
  };

  _removePill = (pill) => {
    this.pills = this.pills.filter((it) => !isEqual(it, pill));
  };
}

export default new FilterStore();
